/**
 * Custom Quick View Modal — Urban Dén Style
 * Fetches product data via Shopify's Product JSON API and renders it in a modal.
 */
(function () {
    'use strict';

    const overlay = document.getElementById('CustomQuickView');
    if (!overlay) return;

    const modal = overlay.querySelector('.cqv-modal');
    const closeBtn = overlay.querySelector('.cqv-close');
    const loadingEl = overlay.querySelector('.cqv-loading');
    const bodyEl = overlay.querySelector('.cqv-body');

    // Elements
    const mainImg = document.getElementById('cqvMainImg');
    const thumbList = document.getElementById('cqvThumbList');
    const titleEl = document.getElementById('cqvTitle');
    const badgesEl = document.getElementById('cqvBadges');
    const priceEl = document.getElementById('cqvPrice');
    const comparePriceEl = document.getElementById('cqvComparePrice');
    const variantsSection = document.getElementById('cqvVariants');
    const variantLabel = document.getElementById('cqvVariantLabel');
    const variantOptions = document.getElementById('cqvVariantOptions');
    const qtyInput = document.getElementById('cqvQty');
    const qtyHidden = document.getElementById('cqvQtyHidden');
    const subtotalEl = document.getElementById('cqvSubtotal');
    const variantIdInput = document.getElementById('cqvVariantId');
    const atcBtn = document.getElementById('cqvAtcBtn');
    const form = document.getElementById('cqvForm');
    const shareBtn = overlay.querySelector('.cqv-share-btn');

    const prevBtn = overlay.querySelector('.cqv-thumb-prev');
    const nextBtn = overlay.querySelector('.cqv-thumb-next');

    let productData = null;
    let currentVariant = null;
    let currentImageIndex = 0;
    let thumbOffset = 0;
    const VISIBLE_THUMBS = 4;

    // ============ OPEN / CLOSE ============
    function open(productUrl) {
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        loadingEl.classList.remove('hidden');
        bodyEl.classList.remove('is-loaded');
        fetchProduct(productUrl);
    }

    function close() {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        productData = null;
    }

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
    });
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    // ============ FETCH PRODUCT ============
    function fetchProduct(url) {
        // Use .js endpoint (not .json) — it includes variant availability
        const jsUrl = url.split('?')[0].replace(/\/$/, '') + '.js';

        fetch(jsUrl)
            .then((res) => res.json())
            .then((data) => {
                // .js endpoint returns product data at root level (not nested under .product)
                productData = data;
                currentVariant = productData.variants[0];
                render();
            })
            .catch((err) => {
                console.error('Quick View fetch error:', err);
                loadingEl.innerHTML = '<p style="color:#999;font-size:14px;">Failed to load product.</p>';
            });
    }

    // ============ RENDER ============
    function render() {
        if (!productData) return;

        // Title
        titleEl.textContent = productData.title;

        // Images — .js endpoint returns images as an array of URL strings
        const images = productData.images || [];
        if (images.length > 0) {
            currentImageIndex = 0;
            mainImg.src = getSizedImageUrl(getImageSrc(images[0]), '800x');
            mainImg.alt = productData.title;
            renderThumbnails(images);
        }

        // Badges
        renderBadges();

        // Price
        updatePrice();

        // Variants
        renderVariants();

        // Quantity
        qtyInput.value = 1;
        qtyHidden.value = 1;
        updateSubtotal();

        // Variant ID
        variantIdInput.value = currentVariant.id;

        // Show body, hide loading
        loadingEl.classList.add('hidden');
        bodyEl.classList.add('is-loaded');
    }

    // ============ IMAGES ============
    function renderThumbnails(images) {
        thumbList.innerHTML = '';
        thumbOffset = 0;

        images.forEach((img, i) => {
            const div = document.createElement('div');
            div.className = 'cqv-thumb' + (i === 0 ? ' is-active' : '');
            div.innerHTML = `<img src="${getSizedImageUrl(getImageSrc(img), '120x')}" alt="">`;
            div.addEventListener('click', () => selectImage(i));
            thumbList.appendChild(div);
        });

        updateThumbVisibility();
    }

    function selectImage(index) {
        const images = productData.images;
        if (!images || !images[index]) return;
        currentImageIndex = index;
        mainImg.src = getSizedImageUrl(getImageSrc(images[index]), '800x');
        mainImg.alt = productData.title;

        thumbList.querySelectorAll('.cqv-thumb').forEach((t, i) => {
            t.classList.toggle('is-active', i === index);
        });
    }

    function updateThumbVisibility() {
        const thumbs = thumbList.querySelectorAll('.cqv-thumb');
        thumbs.forEach((t, i) => {
            t.style.display = i >= thumbOffset && i < thumbOffset + VISIBLE_THUMBS ? '' : 'none';
        });
    }

    prevBtn.addEventListener('click', () => {
        const images = productData?.images || [];
        if (images.length > 0 && currentImageIndex > 0) {
            selectImage(currentImageIndex - 1);
            // Adjust thumb offset if needed
            if (currentImageIndex < thumbOffset) {
                thumbOffset = currentImageIndex;
                updateThumbVisibility();
            }
        }
    });

    nextBtn.addEventListener('click', () => {
        const images = productData?.images || [];
        if (images.length > 0 && currentImageIndex < images.length - 1) {
            selectImage(currentImageIndex + 1);
            // Adjust thumb offset if needed
            if (currentImageIndex >= thumbOffset + VISIBLE_THUMBS) {
                thumbOffset = currentImageIndex - VISIBLE_THUMBS + 1;
                updateThumbVisibility();
            }
        }
    });

    // ============ BADGES ============
    function renderBadges() {
        badgesEl.innerHTML = '';

        if (currentVariant.sku) {
            badgesEl.innerHTML += `<span class="cqv-badge cqv-badge--stock">Product code: ${currentVariant.sku}</span>`;
        } else if (currentVariant.available) {
            badgesEl.innerHTML += '<span class="cqv-badge cqv-badge--stock">In stock</span>';
        } else {
            badgesEl.innerHTML += '<span class="cqv-badge cqv-badge--stock">Sold out</span>';
        }

        if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
            const saved = currentVariant.compare_at_price - currentVariant.price;
            badgesEl.innerHTML += `<span class="cqv-badge cqv-badge--save">Save ${formatMoney(saved)}</span>`;
        }
    }

    // ============ PRICE ============
    function updatePrice() {
        priceEl.textContent = formatMoney(currentVariant.price);

        if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
            comparePriceEl.textContent = formatMoney(currentVariant.compare_at_price);
            comparePriceEl.style.display = '';
        } else {
            comparePriceEl.textContent = '';
            comparePriceEl.style.display = 'none';
        }
    }

    // ============ VARIANTS ============
    function renderVariants() {
        if (productData.variants.length <= 1 && productData.options.length === 1 && productData.options[0].values.length === 1) {
            variantsSection.style.display = 'none';
            return;
        }

        variantsSection.style.display = '';

        // Find the first option (usually "Color")
        const firstOption = productData.options[0];
        variantLabel.textContent = `Select ${firstOption.name}:`;
        variantOptions.innerHTML = '';

        // Group variants by first option
        const optionValues = [];
        const seen = {};

        productData.variants.forEach((v) => {
            const val = v.option1;
            if (!seen[val]) {
                seen[val] = true;
                optionValues.push({ value: val, variant: v });
            }
        });

        optionValues.forEach((opt) => {
            const div = document.createElement('div');
            div.className = 'cqv-variant-swatch' + (opt.variant.id === currentVariant.id ? ' is-active' : '');

            // Try to find a variant image
            const variantImage = opt.variant.featured_image;
            const variantImgSrc = getImageSrc(variantImage);
            if (variantImgSrc) {
                div.innerHTML = `<img src="${getSizedImageUrl(variantImgSrc, '120x')}" alt="${opt.value}">`;
            } else {
                // Color swatch fallback
                div.classList.add('cqv-variant-swatch--color');
                div.style.background = opt.value.toLowerCase();
                div.title = opt.value;
            }

            div.addEventListener('click', () => {
                currentVariant = opt.variant;
                variantIdInput.value = currentVariant.id;
                updatePrice();
                renderBadges();
                updateSubtotal();

                // Update active state
                variantOptions.querySelectorAll('.cqv-variant-swatch').forEach((s) => s.classList.remove('is-active'));
                div.classList.add('is-active');

                // Update main image if variant has featured image
                if (variantImgSrc) {
                    mainImg.src = getSizedImageUrl(variantImgSrc, '800x');
                    mainImg.alt = opt.value;
                }

                // Update ATC button state
                atcBtn.disabled = !currentVariant.available;
                atcBtn.querySelector('.cqv-atc-text').textContent = currentVariant.available ? 'ENQUIRE NOW' : 'SOLD OUT';
            });

            variantOptions.appendChild(div);
        });
    }

    // ============ QUANTITY ============
    overlay.querySelector('.cqv-qty-minus').addEventListener('click', () => {
        let val = parseInt(qtyInput.value) || 1;
        if (val > 1) val--;
        qtyInput.value = val;
        qtyHidden.value = val;
        updateSubtotal();
    });

    overlay.querySelector('.cqv-qty-plus').addEventListener('click', () => {
        let val = parseInt(qtyInput.value) || 1;
        val++;
        qtyInput.value = val;
        qtyHidden.value = val;
        updateSubtotal();
    });

    qtyInput.addEventListener('change', () => {
        let val = parseInt(qtyInput.value) || 1;
        if (val < 1) val = 1;
        qtyInput.value = val;
        qtyHidden.value = val;
        updateSubtotal();
    });

    function updateSubtotal() {
        if (!currentVariant) return;
        const qty = parseInt(qtyInput.value) || 1;
        const total = currentVariant.price * qty;
        subtotalEl.textContent = formatMoney(total);
    }

    // ============ ENQUIRE NOW ============
    form.addEventListener('submit', function (e) {
        e.preventDefault();

        if (!currentVariant || !currentVariant.available) return;

        const atcText = atcBtn.querySelector('.cqv-atc-text');
        const atcSpinner = atcBtn.querySelector('.cqv-atc-spinner');
        atcText.style.display = 'none';
        atcSpinner.style.display = '';
        atcBtn.disabled = true;

        const formData = {
            items: [
                {
                    id: parseInt(variantIdInput.value),
                    quantity: parseInt(qtyHidden.value),
                },
            ],
        };

        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        })
            .then((res) => res.json())
            .then(() => {
                atcText.textContent = '✓ ADDED!';
                atcText.style.display = '';
                atcSpinner.style.display = 'none';

                setTimeout(() => {
                    atcText.textContent = 'ENQUIRE NOW';
                    atcBtn.disabled = false;
                    close();
                    // Refresh cart count
                    fetch('/cart.js')
                        .then((r) => r.json())
                        .then((cart) => {
                            const countEls = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
                            countEls.forEach((el) => (el.textContent = cart.item_count));
                        });
                }, 1200);
            })
            .catch(() => {
                atcText.textContent = 'ERROR';
                atcText.style.display = '';
                atcSpinner.style.display = 'none';
                setTimeout(() => {
                    atcText.textContent = 'ENQUIRE NOW';
                    atcBtn.disabled = false;
                }, 2000);
            });
    });

    // ============ ACTIONS ============
    if (shareBtn) {
        shareBtn.addEventListener('click', () => {
            if (!productData) return;
            const productUrl = window.location.origin + productData.url;

            // Fallback for clipboard if navigator.clipboard is unavailable
            if (navigator.clipboard) {
                navigator.clipboard.writeText(productUrl).then(showCopiedBadge).catch(err => console.error('Failed to copy', err));
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = productUrl;
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand('copy');
                    showCopiedBadge();
                } catch (err) {
                    console.error('Fallback copy failed', err);
                }
                document.body.removeChild(textArea);
            }

            function showCopiedBadge() {
                // Remove existing badge if present
                const existing = shareBtn.parentNode.querySelector('.cqv-copied-badge');
                if (existing) existing.remove();

                const badge = document.createElement('span');
                badge.className = 'cqv-copied-badge';
                badge.textContent = 'Copied!';
                badge.style.position = 'absolute';
                badge.style.top = '-25px';
                badge.style.right = '0';
                badge.style.background = '#222';
                badge.style.color = '#fff';
                badge.style.fontSize = '12px';
                badge.style.padding = '2px 6px';
                badge.style.borderRadius = '3px';
                badge.style.whiteSpace = 'nowrap';
                badge.style.pointerEvents = 'none';
                badge.style.animation = 'fadein 0.2s';

                shareBtn.parentNode.style.position = 'relative';
                shareBtn.parentNode.appendChild(badge);

                setTimeout(() => {
                    if (badge.parentNode) badge.remove();
                }, 2000);
            }
        });
    }

    // ============ HELPERS ============
    // Handle both string URLs (from .js endpoint) and image objects (from .json endpoint)
    function getImageSrc(img) {
        if (!img) return '';
        if (typeof img === 'string') return img;
        if (img.src) return img.src;
        return '';
    }
    function formatMoney(cents) {
        // Shopify product JSON returns prices in cents (or smallest currency unit)
        const amount = (cents / 100).toFixed(2);
        // Use the shop's money format if available
        return '$' + amount;
    }

    function getSizedImageUrl(url, size) {
        if (!url) return '';
        // Insert size before extension, e.g. image.jpg -> image_800x.jpg
        return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
    }

    // ============ GLOBAL OPEN FUNCTION ============
    window.openCustomQuickView = function (productUrl) {
        open(productUrl);
    };
})();
